import { Menu } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import UilEllipsisV from '@iconscout/react-unicons/icons/uil-ellipsis-v';
import propTypes from 'prop-types';
import UilUsersAlt from '@iconscout/react-unicons/icons/uil-users-alt';
import EnvelopeAdd from '@iconscout/react-unicons/icons/uil-envelope-add';
import {
  UilBagAlt,
  UilImages,
  UilLayerGroup,
  UilClipboardAlt,
  UilDocumentLayoutLeft,
  UilUsdCircle,
  UilSetting,
  UilHome,
  UilUser,
  UilLanguage,
  UilCalendarAlt,
  UilLockAlt,
  UilShield,
} from '@iconscout/react-unicons';
// Removed role-based menu gating; now controlled by localStorage permissions

function MenuItems({ toggleCollapsed }) {
  const { t } = useTranslation();

  function getItem(label, key, icon, children, type) {
    return {
      key,
      icon,
      children,
      label,
      type,
    };
  }

  const { topMenu } = useSelector((state) => {
    return {
      topMenu: state.ChangeLayoutMode.topMenu,
    };
  });

  const path = '/admin';

  const pathName = window.location.pathname;
  const pathArray = pathName.split(path);
  const mainPath = pathArray[1];
  const mainPathSplit = mainPath.split('/');

  const [openKeys, setOpenKeys] = React.useState(
    !topMenu
      ? [`${mainPathSplit.length > 2 ? mainPathSplit[1] : 'dashboard'}`]
      : []
  );

  const onOpenChange = (keys) => {
    setOpenKeys(
      keys[keys.length - 1] !== 'recharts'
        ? [keys.length && keys[keys.length - 1]]
        : keys
    );
  };

  const onClick = (item) => {
    if (item.keyPath.length === 1) setOpenKeys([]);
  };

  const studentsChildMenu = [
    {
      label: 'Fresh Leads',
      route: 'fresh-leads',
      icon: <UilLayerGroup />,
    },
    {
      label: 'Ready for Aptitude Test',
      route: 'ready-for-aptitude-test',
      icon: <UilLayerGroup />,
    },
    {
      label: 'Aptitude Test Submitted',
      route: 'aptitude-test-submitted',
      icon: <UilLayerGroup />,
    },
   
    {
      label: 'Ready for Interview',
      route: 'ready-for-interview',
      icon: <UilLayerGroup />,
    },
    {
      label: 'Ready for Enrollment',
      route: 'ready-for-enrollment',
      icon: <UilLayerGroup />,
    },
    {
      label: 'Enrolled With HBIMS',
      route: 'enrolled-with-hbims',
      icon: <UilLayerGroup />,
    },
    {
      label: 'Soft Deleted Students',
      route: 'soft-deleted-students',
      icon: <UilLayerGroup />,
    },
    {
      label: 'Paid Fresh Leads',
      route: 'paid-fresh-leads',
      icon: <UilLayerGroup />,
    },
    {
      label: 'Cold Leads',
      route: 'cold-leads',
      icon: <UilLayerGroup />,
    },
  ];

  // Read permissions from localStorage
  const permissions = React.useMemo(() => {
    try {
      const raw = localStorage.getItem('permissions');
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return {};
    }
  }, []);

  const hasView = (key) => Boolean(permissions?.[key]?.view);
  const childAllowed = (parentKey, childKey) =>
    Boolean(permissions?.[parentKey]?.children?.[childKey]);

  const items = [];

  if (hasView('placements')) {
    items.push(
      getItem(
        <NavLink onClick={toggleCollapsed} to={`${path}/placements`}>
          {t('Placements')}
        </NavLink>,
        'placements',
        !topMenu && (
          <NavLink className="menuItem-iocn" to={`${path}/placements`}>
            <UilUsersAlt />
          </NavLink>
        )
      )
    );
  }

  if (hasView('certificates')) {
    items.push(
      getItem(
        <NavLink onClick={toggleCollapsed} to={`${path}/certificates`}>
          {t('Certificates')}
        </NavLink>,
        'certificates',
        !topMenu && (
          <NavLink className="menuItem-iocn" to={`${path}/certificates`}>
            <UilUsdCircle />
          </NavLink>
        )
      )
    );
  }

  if (hasView('state-languages')) {
    items.push(
      getItem(
        <NavLink onClick={toggleCollapsed} to={`${path}/state-languages`}>
          {t('States & Languages')}
        </NavLink>,
        'state-languages',
        !topMenu && (
          <NavLink className="menuItem-iocn" to={`${path}/state-languages`}>
            <UilLanguage />
          </NavLink>
        )
      )
    );
  }

  if (hasView('communications')) {
    const commChildren = [];
    if (childAllowed('communications', 'templates')) {
      commChildren.push(
        getItem(
          <NavLink onClick={toggleCollapsed} to={`${path}/templates`}>
            {t('Templates')}
          </NavLink>,
          'templates',
          !topMenu && (
            <NavLink className="menuItem-iocn" to={`${path}/templates`}>
              <UilDocumentLayoutLeft />
            </NavLink>
          )
        )
      );
    }
    if (childAllowed('communications', 'triggers')) {
      commChildren.push(
        getItem(
          <NavLink onClick={toggleCollapsed} to={`${path}/triggers`}>
            {t('Triggers')}
          </NavLink>,
          'triggers',
          !topMenu && (
            <NavLink className="menuItem-iocn" to={`${path}/triggers`}>
              <UilDocumentLayoutLeft />
            </NavLink>
          )
        )
      );
    }
    items.push(
      getItem(
        t('Communication'),
        'communications',
        !topMenu && <EnvelopeAdd />,
        commChildren
      )
    );
  }

  if (hasView('home')) {
    const homeChildren = [];
    if (childAllowed('home', 'home/landing-images')) {
      homeChildren.push(
        getItem(
          <NavLink onClick={toggleCollapsed} to={`${path}/home/landing-images`}>
            Hero Section Image
          </NavLink>,
          'landing-images',
          !topMenu && (
            <NavLink
              className="menuItem-iocn"
              to={`${path}/home/landing-images`}
            >
              <UilLayerGroup />
            </NavLink>
          )
        )
      );
    }
    if (childAllowed('home', 'home/gallery-images')) {
      homeChildren.push(
        getItem(
          <NavLink onClick={toggleCollapsed} to={`${path}/home/gallery-images`}>
            {t('Why Merchant Navy')} 
          </NavLink>,
          'gallery-images',
          !topMenu && (
            <NavLink
              className="menuItem-iocn"
              to={`${path}/home/gallery-images`}
            >
              <UilImages />
            </NavLink>
          )
        )
      );
    }
    if (childAllowed('home', 'home/reviews')) {
      homeChildren.push(
        getItem(
          <NavLink onClick={toggleCollapsed} to={`${path}/home/reviews`}>
            {t('What Our Cadets Say')}
          </NavLink>,
          'reviews',
          !topMenu && (
            <NavLink className="menuItem-iocn" to={`${path}/home/reviews`}>
              <UilClipboardAlt />
            </NavLink>
          )
        )
      );
    }
    if (childAllowed('home', 'home/blogs')) {
      homeChildren.push(
        getItem(
          <NavLink onClick={toggleCollapsed} to={`${path}/home/blogs`}>
            {t('Maritime Insights')}
          </NavLink>,
          'blogs',
          !topMenu && (
            <NavLink className="menuItem-iocn" to={`${path}/home/blogs`}>
              <UilDocumentLayoutLeft />
            </NavLink>
          )
        )
      );
    }
    if (childAllowed('home', 'home/placement-companies')) {
      homeChildren.push(
        getItem(
          <NavLink
            onClick={toggleCollapsed}
            to={`${path}/home/placement-companies`}
          >
            {t('Placement Companies')}
          </NavLink>,
          'placement-companies',
          !topMenu && (
            <NavLink
              className="menuItem-iocn"
              to={`${path}/home/placement-companies`}
            >
              <UilDocumentLayoutLeft />
            </NavLink>
          )
        )
      );
    }
    if (childAllowed('home', 'home/fleet-list')) {
      homeChildren.push(
        getItem(
          <NavLink onClick={toggleCollapsed} to={`${path}/home/fleet-list`}>
            {t('Fleet List')}
          </NavLink>,
          'fleet-list',
          !topMenu && (
            <NavLink className="menuItem-iocn" to={`${path}/home/fleet-list`}>
              <UilDocumentLayoutLeft />
            </NavLink>
          )
        )
      );
    }
    if (childAllowed('home', 'home/news')) {
      homeChildren.push(
        getItem(
          <NavLink onClick={toggleCollapsed} to={`${path}/home/news`}>
            {t('HBIMS Update')}
          </NavLink>,
          'news',
          !topMenu && (
            <NavLink className="menuItem-iocn" to={`${path}/home/news`}>
              <UilDocumentLayoutLeft />
            </NavLink>
          )
        )
      );
    }
    items.push(
      getItem(t('Home'), 'home', !topMenu && <UilHome />, homeChildren)
    );
  }

  if (hasView('students')) {
    const studentChildren = studentsChildMenu
      .filter((item) => childAllowed('students', item.route))
      .map((item) =>
        getItem(
          <NavLink onClick={toggleCollapsed} to={`${path}/${item.route}`}>
            {item.label}
          </NavLink>,
          item.route,
          !topMenu && (
            <NavLink className="menuItem-iocn" to={`${path}/${item.route}`}>
              {item.icon}
            </NavLink>
          )
        )
      );
    items.push(
      getItem(
        t('Students'),
        'students',
        !topMenu && <UilUser />,
        studentChildren
      )
    );
  }

  if (hasView('quizzes')) {
    const quizChildren = [];
    if (childAllowed('quizzes', 'quiz')) {
      quizChildren.push(
        getItem(
          <NavLink onClick={toggleCollapsed} to={`${path}/quiz`}>
            {t('Quiz')}
          </NavLink>,
          'quiz',
          !topMenu && (
            <NavLink className="menuItem-iocn" to={`${path}/quiz`}>
              <UilDocumentLayoutLeft />
            </NavLink>
          )
        )
      );
    }
    items.push(
      getItem(t('Quizzes'), 'quizzes', !topMenu && <UilBagAlt />, quizChildren)
    );
  }

  if (hasView('hr-interview-dates')) {
    items.push(
      getItem(
        <NavLink onClick={toggleCollapsed} to={`${path}/hr-interview-dates`}>
          {t('HR Interview Dates')}
        </NavLink>,
        'hr-interview-dates',
        !topMenu && (
          <NavLink className="menuItem-iocn" to={`${path}/hr-interview-dates`}>
            <UilCalendarAlt />
          </NavLink>
        )
      )
    );
  }

  if (hasView('partner-mtis')) {
    items.push(
      getItem(
        <NavLink onClick={toggleCollapsed} to={`${path}/partner-mtis`}>
          Partner MTI'S
        </NavLink>,
        'partner-mtis',
        !topMenu && (
          <NavLink className="menuItem-iocn" to={`${path}/partner-mtis`}>
            <UilUsersAlt />
          </NavLink>
        )
      )
    );
  }

  if (hasView('settings')) {
    const settingChildren = [];
    if (childAllowed('settings', 'users')) {
      settingChildren.push(
        getItem(
          <NavLink onClick={toggleCollapsed} to={`${path}/users`}>
            {t('Users')}
          </NavLink>,
          'users',
          !topMenu && (
            <NavLink className="menuItem-iocn" to={`${path}/users`}>
              <UilUsersAlt />
            </NavLink>
          )
        )
      );
    }
    if (childAllowed('settings', 'leave')) {
      settingChildren.push(
        getItem(
          <NavLink onClick={toggleCollapsed} to={`${path}/leave`}>
            {t('Leave')}
          </NavLink>,
          'leave',
          !topMenu && (
            <NavLink className="menuItem-iocn" to={`${path}/leave`}>
              <UilUsersAlt />
            </NavLink>
          )
        )
      );
    }
    if (childAllowed('settings', 'incentive')) {
    settingChildren.push(
      getItem(
        <NavLink onClick={toggleCollapsed} to={`${path}/incentive`}>
          {t('Incentive')}
        </NavLink>,
        'incentive',
        !topMenu && (
          <NavLink className="menuItem-iocn" to={`${path}/incentive`}>
           <UilUsersAlt />
          </NavLink>
        )
      )
    );
   }
    if (childAllowed('settings', 'roles')) {
      settingChildren.push(
        getItem(
          <NavLink onClick={toggleCollapsed} to={`${path}/roles`}>
            {t('Roles')}
          </NavLink>,
          'roles',
          !topMenu && (
            <NavLink className="menuItem-iocn" to={`${path}/roles`}>
              <UilLockAlt />
            </NavLink>
          )
        )
      );
    }
    if (childAllowed('settings', 'role-permissions')) {
      settingChildren.push(
        getItem(
          <NavLink onClick={toggleCollapsed} to={`${path}/role-permissions`}>
            {t('Role Permissions')}
          </NavLink>,
          'role-permissions',
          !topMenu && (
            <NavLink className="menuItem-iocn" to={`${path}/role-permissions`}>
              <UilShield />
            </NavLink>
          )
        )
      );
    }
    items.push(
      getItem(
        t('Settings'),
        'settings',
        !topMenu && <UilSetting />,
        settingChildren
      )
    );
  }

  return (
    <Menu
      onOpenChange={onOpenChange}
      onClick={onClick}
      mode={!topMenu || window.innerWidth <= 991 ? 'inline' : 'horizontal'}
      // // eslint-disable-next-line no-nested-ternary
      defaultSelectedKeys={
        !topMenu
          ? [
              `${
                mainPathSplit.length === 1
                  ? 'home'
                  : mainPathSplit.length === 2
                  ? mainPathSplit[1]
                  : mainPathSplit[2]
              }`,
            ]
          : []
      }
      defaultOpenKeys={
        !topMenu
          ? [`${mainPathSplit.length > 2 ? mainPathSplit[1] : 'dashboard'}`]
          : []
      }
      overflowedIndicator={<UilEllipsisV />}
      openKeys={openKeys}
      items={items}
    />
  );
}

MenuItems.propTypes = {
  toggleCollapsed: propTypes.func,
};

export default MenuItems;
